import React, { useEffect, useMemo, useState } from "react";
import { NewsletterEdition } from "../types/newsletterEdition";
import campaignService from "../services/campaignService";

export interface NewsletterApprovalConfirmModalProps {
  editionId: string;
  newsletterTitle: string;
  scheduledSendDate?: string | null;
  /** All of the org's editions, used to warn about closely scheduled sends. */
  allEditions?: NewsletterEdition[] | null;
  /** Called with an ISO date when the user picked or changed the send date; undefined when unchanged. */
  onConfirm: (selectedSendDate?: string) => void;
  onCancel: () => void;
}

const NewsletterApprovalConfirmModal: React.FC<NewsletterApprovalConfirmModalProps> = ({
  editionId,
  newsletterTitle,
  scheduledSendDate,
  allEditions,
  onConfirm,
  onCancel,
}) => {
  const needsDate = !scheduledSendDate;
  // Local YYYY-MM-DD of the currently scheduled date, or "" when none is set.
  const initialDate = scheduledSendDate
    ? new Date(scheduledSendDate).toLocaleDateString("en-CA")
    : "";
  const [selectedDate, setSelectedDate] = useState<string>(initialDate);
  const dateChanged = selectedDate !== initialDate;

  // The modal can mount before the edition finishes loading (e.g. the email
  // magic link opens it on page load), so re-sync the input when the
  // scheduled date arrives or changes.
  useEffect(() => {
    setSelectedDate(initialDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduledSendDate]);

  // Anchor the picked calendar date to noon local time so it doesn't shift a day
  // when converted to UTC. Keep the original timestamp while the date is untouched.
  const selectedIso = selectedDate
    ? new Date(`${selectedDate}T12:00:00`).toISOString()
    : null;
  const effectiveSendDate = !dateChanged && scheduledSendDate
    ? scheduledSendDate
    : selectedIso;

  const conflictEdition = useMemo(() => {
    if (!allEditions || !effectiveSendDate) return null;
    return campaignService.findCloseScheduledEdition(allEditions, {
      id: editionId,
      scheduled_send_start_date: effectiveSendDate,
    } as NewsletterEdition);
  }, [allEditions, editionId, effectiveSendDate]);

  const formatDate = (date: string) => new Date(date).toLocaleDateString();
  const todayLocal = new Date().toLocaleDateString("en-CA"); // YYYY-MM-DD

  const daysApartText = conflictEdition
    ? conflictEdition.daysApart === 0
      ? "the same day as this one"
      : `only ${conflictEdition.daysApart} day${conflictEdition.daysApart === 1 ? "" : "s"} from this one`
    : null;

  const approveDisabled = !selectedDate;

  return (
    <div
      className="primary-input-container"
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        padding: "32px",
        maxWidth: "350px",
      }}
    >
      <h2 style={{ textAlign: "center" }}>Confirm Approval</h2>
      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}>
        <p style={{
          fontWeight: "bold",
          margin: "0",
          textAlign: "center",
          overflowWrap: "break-word"
        }}>{newsletterTitle}</p>
        <p style={{ margin: "0", textAlign: "center" }}>
          {needsDate
            ? <>This newsletter doesn't have a send date yet. Please pick one to approve it.</>
            : <>This newsletter is scheduled to send on <strong>{formatDate(scheduledSendDate!)}</strong>. You can change the date below.</>}
        </p>
        <input
          type="date"
          value={selectedDate}
          min={todayLocal}
          onChange={(e) => setSelectedDate(e.target.value)}
          style={{ padding: "8px", fontSize: "16px" }}
        />
        {!needsDate && dateChanged && selectedDate && (
          <p style={{ margin: "0", textAlign: "center", fontSize: "14px" }}>
            The send date will be updated to <strong>{formatDate(`${selectedDate}T12:00:00`)}</strong> when you approve.
          </p>
        )}
        {conflictEdition && (
          <p style={{
            margin: "0",
            padding: "12px",
            borderRadius: "8px",
            backgroundColor: "#FFF4E5",
            border: "1px solid #F5C77E",
            overflowWrap: "break-word",
          }}>
            ⚠️ <strong>{conflictEdition.title}</strong> is already approved and
            scheduled to send on <strong>{formatDate(conflictEdition.scheduledSendDate)}</strong> — {daysApartText}.
            Do you still want to send two newsletters this close together?
          </p>
        )}
        <button
          className="primary-button"
          disabled={approveDisabled}
          style={approveDisabled ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
          onClick={() => {
            if (approveDisabled) return;
            onConfirm(dateChanged ? selectedIso! : undefined);
          }}
        >
          {conflictEdition ? "Approve anyway" : "Approve"}
        </button>
        <button className="tertiary-button" onClick={onCancel}>Close</button>
      </div>
    </div>
  );
};

export default NewsletterApprovalConfirmModal;
